import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, FileText, CheckCircle, Minus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * Year-over-Year Comparison Component
 * Shows current vs previous period with growth indicators
 */
export default function YoYComparison({ period = 'monthly' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchYoYData();
  }, [period]);

  const fetchYoYData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/advanced/yoy-comparison?period=${period}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error fetching YoY data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-slate-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const metrics = [
    {
      label: 'Scenarios',
      icon: FileText,
      current: data.current_period.scenario_count,
      previous: data.previous_period.scenario_count,
      growth: data.growth.scenario_count,
      format: (val) => val.toLocaleString(),
    },
    {
      label: 'Gross Revenue',
      icon: DollarSign,
      current: data.current_period.gross_revenue,
      previous: data.previous_period.gross_revenue,
      growth: data.growth.gross_revenue,
      format: (val) => `$${(val / 1000).toFixed(1)}K`,
    },
    {
      label: 'Net Revenue',
      icon: DollarSign,
      current: data.current_period.net_revenue,
      previous: data.previous_period.net_revenue,
      growth: data.growth.net_revenue,
      format: (val) => `$${(val / 1000).toFixed(1)}K`,
    },
    {
      label: 'Approval Rate',
      icon: CheckCircle,
      current: data.current_period.approval_rate,
      previous: data.previous_period.approval_rate,
      growth: data.growth.approval_rate,
      format: (val) => `${val.toFixed(1)}%`,
    },
  ];

  const chartData = [
    {
      name: 'Previous Period',
      Scenarios: data.previous_period.scenario_count,
      'Gross Revenue': data.previous_period.gross_revenue / 1000,
      'Net Revenue': data.previous_period.net_revenue / 1000,
    },
    {
      name: 'Current Period',
      Scenarios: data.current_period.scenario_count,
      'Gross Revenue': data.current_period.gross_revenue / 1000,
      'Net Revenue': data.current_period.net_revenue / 1000,
    },
  ];

  const GrowthIndicator = ({ value }) => {
    if (value > 0) {
      return (
        <div className="flex items-center gap-1 text-green-600">
          <TrendingUp className="w-4 h-4" />
          <span className="text-sm font-medium">+{value.toFixed(1)}%</span>
        </div>
      );
    } else if (value < 0) {
      return (
        <div className="flex items-center gap-1 text-red-600">
          <TrendingDown className="w-4 h-4" />
          <span className="text-sm font-medium">{value.toFixed(1)}%</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 text-slate-400">
          <Minus className="w-4 h-4" />
          <span className="text-sm font-medium">0%</span>
        </div>
      );
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
      <h3 className="text-lg font-bold text-slate-900 mb-6">
        Year-over-Year Comparison ({period})
      </h3>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <metric.icon className="w-5 h-5 text-blue-600" />
              </div>
              <GrowthIndicator value={metric.growth} />
            </div>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{metric.label}</p>
            <p className="text-2xl font-bold text-slate-900">{metric.format(metric.current)}</p>
            <p className="text-xs text-slate-400 mt-1">
              vs {metric.format(metric.previous)}
            </p>
          </div>
        ))}
      </div>

      {/* Comparison Chart */}
      <div className="mt-6">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" style={{ fontSize: '12px' }} />
            <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Bar dataKey="Scenarios" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Gross Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Net Revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
